import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Clickable from 'components/Common/Clickable'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import ModalContext from 'contexts/ModalContext'
import { partitionBy } from 'lib/array/arrayUtils'
import { type TransportConnection, type TransportOption, isFlightCardSavedItemData } from 'tripPlanner/api/types'
import { useItineraryContext } from '../../contexts/ItineraryContext'
import useRefreshShortlistedFlights from './useRefreshShortlistedFlights'
import type { RefreshedFlight } from './useRefreshShortlistedFlights'
import useBookShortlistedFlight from './useBookShortlistedFlight'
import useViewBookedShortlistedFlight from './useViewBookedShortlistedFlight'
import ShortlistedFlightCard from './ShortlistedFlightCard'
import ShortlistedMultiCityFlightCard from './ShortlistedMultiCityFlightCard'
import { groupShortlistedFlights, type ShortlistedFlightGroup } from './shortlistedMultiCityGrouping'
import { isBookedFlightOption } from './flightShortlistUtils'

const SectionContainer = styled.div`
  width: 100%;
  margin-top: ${rem(12)};
`

const HeaderClickable = styled(Clickable)`
  width: 100%;
`

const HeaderContainer = styled.div<{ $hasTopSpacing: boolean }>`
  margin-top: ${props => (props.$hasTopSpacing ? rem(12) : 0)};
`

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  margin-top: ${rem(12)};
`

const CountCircle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(22)};
  min-width: ${rem(22)};
  padding: ${rem(4)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  color: ${props => props.theme.palette.highlight.primary.lightContrast};
`

const SkeletonSeparator = styled.hr`
  border: none;
  border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  margin: 0;
  width: 100%;
`

function FlightCardSkeleton() {
  return (
    <Sheet border="light-foreground" borderRadius="L" padding={16}>
      <Group direction="vertical" gap={16} fullWidth>
        <Group direction="vertical" gap={4}>
          <LoadingBox height={16} width={120} borderRadius="S" />
          <LoadingBox height={12} width={220} borderRadius="S" />
        </Group>

        <SkeletonSeparator />

        <Group direction="horizontal" gap={16} verticalAlign="center" fullWidth>
          <LoadingBox height={32} width={32} borderRadius="round" />
          <Group direction="vertical" gap={4}>
            <LoadingBox height={16} width={110} borderRadius="S" />
            <LoadingBox height={12} width={80} borderRadius="S" />
          </Group>
          <Group direction="vertical" gap={4}>
            <LoadingBox height={14} width={60} borderRadius="S" />
            <LoadingBox height={12} width={50} borderRadius="S" />
          </Group>
          <LoadingBox height={14} width={50} borderRadius="S" />
        </Group>

        <SkeletonSeparator />

        <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="space-between" fullWidth>
          <LoadingBox height={20} width={90} borderRadius="S" />
          <LoadingBox height={32} width={80} borderRadius="M" />
        </Group>
      </Group>
    </Sheet>
  )
}

function getShortlistedOptions(transport: TransportConnection): Array<TransportOption> {
  return transport.options.filter(o => isFlightCardSavedItemData(o.savedItemData))
}

interface Props {
  transport: TransportConnection
  onDeleteItem: (itemId: number) => void
  /** Hide the collapsible "Shortlisted flights" header and always show the cards. */
  hideHeader?: boolean
}

function ShortlistedFlightsSection({ transport, onDeleteItem, hideHeader = false }: Props) {
  const intl = useIntl()
  const { itineraryData } = useItineraryContext()
  const showModal = useContext(ModalContext)
  const [isExpanded, setIsExpanded] = useState(true)

  const shortlistedOptions = useMemo(
    () => getShortlistedOptions(transport),
    [transport],
  )
  const [bookedOptions, unbookedOptions] = useMemo(
    () => partitionBy(shortlistedOptions, isBookedFlightOption),
    [shortlistedOptions],
  )

  // A multi-city itinerary's legs hang off different transport connections, so
  // completing a group means looking across the whole trip, not just this one.
  const itineraryFlightOptions = useMemo(
    () => (itineraryData?.stops ?? []).flatMap(stop =>
      (stop.transports ?? []).flatMap(getShortlistedOptions),
    ),
    [itineraryData],
  )
  const [bookedItineraryOptions, unbookedItineraryOptions] = useMemo(
    () => partitionBy(itineraryFlightOptions, isBookedFlightOption),
    [itineraryFlightOptions],
  )

  const bookedGroups = useMemo(
    () => groupShortlistedFlights(bookedOptions, bookedItineraryOptions),
    [bookedItineraryOptions, bookedOptions],
  )
  const unbookedGroups = useMemo(
    () => groupShortlistedFlights(unbookedOptions, unbookedItineraryOptions),
    [unbookedItineraryOptions, unbookedOptions],
  )

  // Refresh what is on screen, not just this connection's options: a grouped
  // card shows legs from other connections, and leaving one of them on a stale
  // expiry keeps the whole card looking expired after a successful refresh.
  const refreshTargets = useMemo(
    () => unbookedGroups.flatMap(group => group.kind === 'multiCity' ? group.legs : [group.option]),
    [unbookedGroups],
  )

  const { status: refreshStatus, refreshedFlights, refresh } = useRefreshShortlistedFlights()
  const { bookFlight, bookFlightGroup, bookingItemId, searchingItemId } = useBookShortlistedFlight(showModal)
  const { viewBookedFlightDetails, viewUnbookedFlightDetails, viewingDetailsItemId, flightDetailsDrawer } = useViewBookedShortlistedFlight()

  const hasAutoRefreshed = useRef(false)
  useEffect(() => {
    if (hasAutoRefreshed.current || refreshTargets.length === 0) return
    hasAutoRefreshed.current = true
    refresh(refreshTargets)
  }, [refresh, refreshTargets])

  const isRefreshing = refreshStatus === 'refreshing'

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  const handleRefresh = useCallback(async() => {
    await refresh(refreshTargets)
  }, [refresh, refreshTargets])

  const handleDelete = useCallback((option: TransportOption) => {
    onDeleteItem(option.itemId)
  }, [onDeleteItem])

  const handleDeleteGroup = useCallback((legs: Array<TransportOption>) => {
    legs.forEach(leg => onDeleteItem(leg.itemId))
  }, [onDeleteItem])

  const handleViewUnbookedDetails = useCallback((option: TransportOption, refreshedData?: RefreshedFlight) => {
    viewUnbookedFlightDetails(option, refreshedData, bookFlight)
  }, [viewUnbookedFlightDetails, bookFlight])

  const getRefreshedData = useCallback((option: TransportOption): RefreshedFlight | undefined => {
    return refreshedFlights.find(r => r.option.itemId === option.itemId)
  }, [refreshedFlights])

  const renderShortlistedFlightCard = useCallback((option: TransportOption) => (
    <ShortlistedFlightCard
      key={option.itemId}
      option={option}
      refreshedData={getRefreshedData(option)}
      onBookNow={bookFlight}
      onDelete={handleDelete}
      onRefresh={handleRefresh}
      isBooking={bookingItemId === option.itemId}
      isSearching={searchingItemId === option.itemId}
      onViewBookedDetails={viewBookedFlightDetails}
      onViewUnbookedDetails={handleViewUnbookedDetails}
      isViewingDetails={viewingDetailsItemId === option.itemId}
    />
  ), [
    bookingItemId,
    bookFlight,
    getRefreshedData,
    handleDelete,
    handleRefresh,
    handleViewUnbookedDetails,
    searchingItemId,
    viewBookedFlightDetails,
    viewingDetailsItemId,
  ])

  const renderGroup = (group: ShortlistedFlightGroup) => {
    if (group.kind === 'single') return renderShortlistedFlightCard(group.option)

    return (
      <ShortlistedMultiCityFlightCard
        key={group.key}
        legs={group.legs}
        getRefreshedData={getRefreshedData}
        onBookNow={bookFlightGroup}
        onDelete={handleDeleteGroup}
        onRefresh={handleRefresh}
        isBooking={bookingItemId === group.legs[0]?.itemId}
        isSearching={searchingItemId === group.legs[0]?.itemId}
      />
    )
  }

  if (shortlistedOptions.length === 0) return <>{flightDetailsDrawer}</>

  const hasBookedOptions = bookedOptions.length > 0
  const hasUnbookedOptions = unbookedOptions.length > 0
  const ToggleIcon = isExpanded ? LineAngleUpIcon : LineAngleDownIcon

  const headerLabel = intl.formatMessage({
    id: 'QnzwHKT3FGvP48bzGzPV',
    defaultMessage: 'Shortlisted flights',
    description: 'Header label for the shortlisted flights section in trip planner itinerary',
  })

  return (
    <SectionContainer>
      {hasBookedOptions && (
        <CardsContainer>
          {bookedGroups.map(renderGroup)}
        </CardsContainer>
      )}

      {hasUnbookedOptions && <>
        {!hideHeader && (
          <HeaderContainer $hasTopSpacing={hasBookedOptions}>
            <HeaderClickable onClick={toggleExpanded}>
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <BodyText variant="medium" weight="bold">{headerLabel}</BodyText>
                <CountCircle>
                  <Caption variant="smallalt" weight="bold">{unbookedOptions.length}</Caption>
                </CountCircle>
                <ToggleIcon size="S" />
              </Group>
            </HeaderClickable>
          </HeaderContainer>
        )}

        {(hideHeader || isExpanded) && (
          <CardsContainer>
            {isRefreshing && unbookedGroups.map(group => (
              <FlightCardSkeleton key={group.key} />
            ))}

            {!isRefreshing && unbookedGroups.map(renderGroup)}
          </CardsContainer>
        )}
      </>}
      {flightDetailsDrawer}
    </SectionContainer>
  )
}

export default ShortlistedFlightsSection
